/**
 * Whether a new joiner may launch their Local Storefront yet (NEA-6534).
 *
 * The storefront is only worth launching once it has something to show, so
 * the gate is "at least one in-stock product is indexed" — the same Typesense
 * count the Inventory page headlines (`inStockItems` on the page summary).
 * A sync timestamp on its own is not enough: `latestIngest.timestamp` is
 * stamped on every attempt, including an empty or unparseable first file, and
 * a storefront launched off that would still be empty.
 *
 * States, in the order a new joiner moves through them:
 *   - `no-source`        nothing connected yet → send them to connect a POS
 *   - `awaiting-sync`    POS connected, no file has landed → wait, set expectations
 *   - `products-arriving` a file has landed (lastSuccessfulIngestAt) but the
 *                        index hasn't caught up → the count is about to tick up
 *   - `ready`            in-stock products exist → launch
 *
 * Pure: takes the page summary + shop the caller already has. `connectType`
 * is exposed because it decides how fast the first file can arrive — OAuth
 * connections download on connect, everything else waits on a cron or a
 * human — and the hook uses it to pick a polling cadence. The same type keys
 * the upper-bound wait the card promises ("this should take less than …").
 *
 * The bounds are deliberately generous ceilings, not averages (Max, 13 Oct
 * 2026 — a guesstimate is better than nothing): OAuth fires a download on
 * connect and the pipeline needs ~1–3 min after that; plug-and-play waits for
 * the connector's cron, up to 4–6 h for the slowest (Shopify key, Vend,
 * Lightspeed); partner-lead is the POS provider's own 2–3 working days; and a
 * provider with no `connect` block (FTP / email / direct upload — BMS, Epicor
 * Eagle…) needs someone on the POS side to set the export up, which is the
 * same order of magnitude.
 */

const FIRST_UPLOAD_WITHIN = {
	oauth: '15 minutes',
	'plug-play': '6 hours',
	'partner-lead': '3 working days'
};

const DEFAULT_FIRST_UPLOAD_WITHIN = '3 working days';

export const expectedFirstUploadWithin = (connectType) => FIRST_UPLOAD_WITHIN[connectType] ?? DEFAULT_FIRST_UPLOAD_WITHIN;

/** The one sentence every waiting surface shows (Overview card, Channels welcome, storefront Enable hint). */
export const awaitingUploadCopy = ({ sourceLabel, connectType }) =>
	`Your stock upload is being prepared by ${sourceLabel ?? 'your POS'}. This should take less than ${expectedFirstUploadWithin(connectType)}.`;

export function deriveStorefrontLaunchState({ summary, shop, summaryLoading = false } = {}) {
	const source = summary?.source ?? shop?.stockInventorySource ?? null;
	const hasSource = Boolean(source);
	const inStockItems = Number.isFinite(summary?.inStockItems) ? summary.inStockItems : null;
	const fileLanded = Boolean(summary?.source?.lastSuccessfulIngestAt);

	let state;
	if (inStockItems != null && inStockItems > 0) state = 'ready';
	else if (!hasSource) state = 'no-source';
	else if (summaryLoading || inStockItems == null) state = 'loading';
	else if (fileLanded) state = 'products-arriving';
	else state = 'awaiting-sync';

	// The shop payload carries the provider's display name ("Shopify"); the
	// summary's label is the raw connector id ("shopify"), so it is the fallback.
	const sourceLabel = shop?.stockInventorySource?.providerLabel ?? summary?.source?.label ?? null;
	const connectType = summary?.source?.connectType ?? shop?.stockInventorySource?.connectType ?? null;

	return {
		state,
		canLaunch: state === 'ready',
		waiting: state === 'awaiting-sync' || state === 'products-arriving',
		inStockItems,
		sourceLabel,
		connectType,
		expectedWait: expectedFirstUploadWithin(connectType)
	};
}
